'use client';

import { X } from 'lucide-react';
import Image from 'next/image';
import { useEffect, useState } from 'react';

interface NewsletterSubscriptionResponse {
  success: boolean;
  message: string;
  contactId?: string;
}

export function WelcomeToast() {
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<NewsletterSubscriptionResponse | null>(null);

  useEffect(() => {
    // Check if welcome modal should be shown
    if (window.innerHeight < 650) return;
    
    // Check if user has already seen this version of the modal
    if (!document.cookie.includes('welcome-modal-shown=v2')) {
      setIsVisible(true);
    }
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    // Set cookie to remember user has seen the modal
    document.cookie = 'welcome-modal-shown=v2; max-age=2592000; path=/'; // 30 days
  };

  const handleNoThanks = () => {
    handleClose();
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsSubmitting(true);
    setSubscriptionStatus(null);

    try {
      const response = await fetch('/api/newsletter', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.trim() }),
      });

      const data: NewsletterSubscriptionResponse = await response.json();
      setSubscriptionStatus(data);

      if (data.success) {
        // Auto-close after successful subscription
        setTimeout(() => {
          handleClose();
        }, 2000);
      }
    } catch (error) {
      setSubscriptionStatus({
        success: false,
        message: 'Network error. Please try again later.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Background Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        {/* Modal Container */}
        <div 
          className="relative w-full max-w-[553px] min-h-[538px] max-h-[95vh] overflow-y-auto bg-gradient-to-br from-[#F8F9FA] to-[#F2F9FF] rounded-lg shadow-2xl flex flex-col justify-center items-center p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-col w-full h-full gap-[24px] items-end relative">
            {/* Close Button */}
            <div className="flex justify-end w-full">
              <button
                onClick={handleClose}
                className="p-2 hover:bg-white/50 rounded-full transition-colors"
                aria-label="Close modal"
              >
                <X size={20} className="text-[#73777F]" strokeWidth={2} />
              </button>
            </div>

            {/* Main Content */}
            <div className="flex flex-col items-center w-full gap-[40px] flex-1 justify-center">
              {/* Logo */}
              <div className="mb-0">
                <Image
                  src="/icons/PionPowerLogo.png"
                  alt="Pion Power"
                  width={186}
                  height={20}
                  className="h-5 w-auto"
                />
              </div>

              {/* Confirmation State */}
              {subscriptionStatus?.success ? (
                <div className="flex flex-col items-center w-full gap-6">
                  <div className="flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-[#14BBD3] to-[#263AEA] mb-2">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h2 className="text-[25px] lg:text-[36px] leading-[34px] lg:leading-[43px] font-bold font-futura text-center bg-gradient-to-r from-[#14BBD3] to-[#263AEA] bg-clip-text text-transparent" style={{ WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                    Thank you for subscribing!
                  </h2>
                  <p className="w-full max-w-[505px] text-[16px] lg:text-[20px] leading-[20px] lg:leading-[24px] text-[#73777F] font-futura-md text-center">
                    You’ve been added to our newsletter. Check your inbox for updates and your coupon!
                  </p>
                </div>
              ) : (
                <>
                  {/* Title & Description */}
                  <div className="flex flex-col items-center w-full gap-4">
                    <h1
                      className="w-full max-w-[505px] h-[86px] text-[25px] lg:text-[36px] leading-[34px] lg:leading-[43px] font-bold font-futura mb-0 text-center bg-gradient-to-r from-[#14BBD3] to-[#263AEA] bg-clip-text text-transparent"
                      style={{ WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
                    >
                      SIGN UP TO GET AN EXTRA $10 OFF COUPON
                    </h1>
                    <p className="w-full max-w-[505px] h-[24px] text-[16px] lg:text-[20px] leading-[20px] lg:leading-[24px] text-[#73777F] font-futura-md text-center">
                      Sign up now unlock extra savings on every product.
                    </p>
                  </div>

                  {/* Email Form */}
                  <form onSubmit={handleEmailSubmit} className="w-full max-w-[505px] flex flex-col gap-4">
                    <div className="flex flex-row items-center gap-3 border border-[#73777F] rounded-md px-4 py-2">
                      <input
                        type="email"
                        placeholder="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="flex-1 h-8 bg-transparent outline-none border-none focus:outline-none focus:ring-0 text-[16px] text-[#73777F] font-futura-md placeholder-[#73777F]"
                        required
                        disabled={isSubmitting}
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={isSubmitting || !email.trim()}
                      className="w-full h-[55px] bg-gradient-to-r from-[#14BBD3] to-[#263AEA] rounded-lg text-white font-futura-heavy text-[20px] leading-[28px] flex items-center justify-center transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          <span className="hidden lg:inline">SIGNING UP...</span>
                          <span className="lg:hidden">...</span>
                        </span>
                      ) : (
                        'SIGN UP'
                      )}
                    </button>
                  </form>

                  {/* Disclaimer */}
                  <p className="w-full max-w-[505px] h-[38px] text-[14px] lg:text-[16px] leading-[17px] lg:leading-[19px] text-[#73777F] font-futura-book text-center">
                    *By signing up, you agree to receive promotional emails from Pion Power.
                  </p>

                  {/* Bottom Actions */}
                  <div className="flex flex-row items-center gap-4 w-[100px] mx-auto">
                    <button
                      onClick={handleNoThanks}
                      className="text-[16px] leading-[19px] text-[#73777F] font-futura-md hover:text-[#495057] transition-colors"
                    >
                      No Thanks
                    </button>
                    <div className="w-[5px] h-[10px]">
                      <svg width="5" height="10" viewBox="0 0 5 10" fill="none">
                        <path d="M0 0L5 5L0 10" stroke="#73777F" strokeWidth="2" />
                      </svg>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
